import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { Turnstile } from '@marsidev/react-turnstile';
import axios from 'axios';
import { API_URL } from '../../config/constants';
import { GlassCard, GlassButton, GlassInput, GlassSelect } from '../../components/ui/GlassComponents';

const TURNSTILE_SITE_KEY = '0x4AAAAAADAvVFOq2COJbA6Q';

const Signup = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const { register } = useAuth();
  const navigate = useNavigate();
  
  // Form data
  const [formData, setFormData] = useState({
    // Team info
    teamName: '',
    subscriptionPlan: 'small', // Changed from 'basic' to 'small' to match server expectations
    
    // Admin info
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const validateStep1 = () => {
    if (!formData.teamName.trim()) {
      toast.error('Team name is required');
      return false;
    }
    if (!formData.subscriptionPlan) {
      toast.error('Please select a subscription plan');
      return false;
    }
    return true;
  };
  
  const validateStep2 = () => {
    if (!formData.firstName.trim()) {
      toast.error('First name is required');
      return false;
    }
    if (!formData.lastName.trim()) {
      toast.error('Last name is required');
      return false;
    }
    if (!formData.email.trim()) {
      toast.error('Email is required');
      return false;
    }
    if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      toast.error('Please enter a valid email');
      return false;
    }
    if (!formData.password) {
      toast.error('Password is required');
      return false;
    }
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return false;
    }
    if (!/[A-Z]/.test(formData.password)) {
      toast.error('Password must contain at least one uppercase letter');
      return false;
    }
    if (!/[a-z]/.test(formData.password)) {
      toast.error('Password must contain at least one lowercase letter');
      return false;
    }
    if (!/[0-9]/.test(formData.password)) {
      toast.error('Password must contain at least one number');
      return false;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
      toast.error('Password must contain at least one special character');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    return true;
  };
  
  const nextStep = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    }
  };
  
  const prevStep = () => {
    setStep(1);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep2()) {
      return;
    }
    
    if (!turnstileToken) {
      toast.error('Security check not ready — please wait a moment and try again');
      return;
    }

    setLoading(true);

    try {
      // Special case for dean.omeara@live.co.uk - use the old flow
      if (formData.email === 'dean.omeara@live.co.uk') {
        const result = await register({
          team: {
            name: formData.teamName,
            subscriptionPlan: formData.subscriptionPlan,
          },
          admin: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            password: formData.password,
          }
        }, turnstileToken);

        if (result.success) {
          toast.success('Registration successful!');
          navigate('/dashboard');
        } else {
          toast.error(result.message || 'Registration failed. Please try again.');
          turnstileRef.current?.reset();
          setTurnstileToken(null);
        }
      } else {
        // Use the new secure registration flow for all other users
        // Create a pending registration and redirect to Stripe checkout
        const response = await axios.post(`${API_URL}/registration/initiate`, {
          teamName: formData.teamName,
          plan: formData.subscriptionPlan,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          turnstileToken
        });

        if (response.data.success) {
          toast.success('Registration initiated! Redirecting to payment...');
          sessionStorage.setItem('pendingRegistrationId', response.data.pendingRegistrationId);
          window.location.href = response.data.url;
        } else {
          toast.error(response.data.message || 'Registration failed. Please try again.');
          turnstileRef.current?.reset();
          setTurnstileToken(null);
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('An error occurred during registration. Please try again.');
      }
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="w-full max-w-md space-y-8">
      <div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
          Register your team
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
          Or{' '}
          <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300">
            sign in to your account
          </Link>
        </p>
      </div>
      
      <div className="flex justify-center">
        <ol className="flex items-center w-full">
          <li className={`flex w-full items-center ${step === 1 ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'} after:content-[''] after:w-full after:h-1 after:border-b after:border-gray-200 after:border-1 after:hidden sm:after:inline-block after:mx-6 xl:after:mx-10`}>
            <span className={`flex items-center justify-center w-8 h-8 ${step === 1 ? 'bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400' : 'bg-gray-100 dark:bg-gray-700'} rounded-full lg:h-10 lg:w-10 shrink-0`}>
              1
            </span>
            <span className="hidden sm:inline-flex sm:ml-2">Team</span>
          </li>
          <li className={`flex items-center ${step === 2 ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'}`}>
            <span className={`flex items-center justify-center w-8 h-8 ${step === 2 ? 'bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400' : 'bg-gray-100 dark:bg-gray-700'} rounded-full lg:h-10 lg:w-10 shrink-0`}>
              2
            </span>
            <span className="hidden sm:inline-flex sm:ml-2">Admin</span>
          </li>
        </ol>
      </div>
      
      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label htmlFor="teamName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Team Name
              </label>
              <GlassInput
                id="teamName"
                name="teamName"
                type="text"
                required
                placeholder="Enter your team name"
                value={formData.teamName}
                onChange={handleChange}
              />
            </div>
            
            <div>
              <label className="label">Subscription Plan</label>
              <div className="mt-2 space-y-4">
                <div className="relative flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="small"
                      name="subscriptionPlan"
                      type="radio"
                      value="small"
                      checked={formData.subscriptionPlan === 'small'}
                      onChange={handleChange}
                      className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500 dark:bg-gray-800 dark:border-gray-600"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="small" className="font-medium text-gray-700 dark:text-gray-300">
                      Small
                    </label>
                    <p className="text-gray-500 dark:text-gray-400">Up to 5 team members</p>
                  </div>
                </div>
                
                <div className="relative flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="medium"
                      name="subscriptionPlan"
                      type="radio"
                      value="medium"
                      checked={formData.subscriptionPlan === 'medium'}
                      onChange={handleChange}
                      className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500 dark:bg-gray-800 dark:border-gray-600"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="medium" className="font-medium text-gray-700 dark:text-gray-300">
                      Medium
                    </label>
                    <p className="text-gray-500 dark:text-gray-400">Up to 12 team members</p>
                  </div>
                </div>
                
                <div className="relative flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="large"
                      name="subscriptionPlan"
                      type="radio"
                      value="large"
                      checked={formData.subscriptionPlan === 'large'}
                      onChange={handleChange}
                      className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500 dark:bg-gray-800 dark:border-gray-600"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="large" className="font-medium text-gray-700 dark:text-gray-300">
                      Large
                    </label>
                    <p className="text-gray-500 dark:text-gray-400">Up to 20 team members</p>
                  </div>
                </div>
                
                <div className="relative flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="enterprise"
                      name="subscriptionPlan"
                      type="radio"
                      value="enterprise"
                      checked={formData.subscriptionPlan === 'enterprise'}
                      onChange={handleChange}
                      className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500 dark:bg-gray-800 dark:border-gray-600"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="enterprise" className="font-medium text-gray-700 dark:text-gray-300">
                      Enterprise
                    </label>
                    <p className="text-gray-500 dark:text-gray-400">Up to 100 team members</p>
                  </div>
                </div>
              </div>
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  💡 You can upgrade your plan at any time within the app if you need more team members or additional features.
                </p>
              </div>
            </div>
            
            <div className="pt-4">
              <GlassButton
                type="button"
                onClick={nextStep}
                className="w-full py-3"
              >
                Continue
              </GlassButton>
            </div>
          </div>
        )}
        
        {step === 2 && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="firstName" className="label">
                  First Name
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  required
                  className="input mt-1"
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={handleChange}
                />
              </div>
              
              <div>
                <label htmlFor="lastName" className="label">
                  Last Name
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  required
                  className="input mt-1"
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="email" className="label">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="input mt-1"
                placeholder="Email address"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
            
            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                className="input mt-1"
                placeholder="Create a password"
                value={formData.password}
                onChange={handleChange}
              />
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                <p>Password requirements:</p>
                <ul className="list-disc pl-5 mt-1">
                  <li>At least 8 characters</li>
                  <li>At least one uppercase letter</li>
                  <li>At least one lowercase letter</li>
                  <li>At least one number</li>
                  <li>At least one special character (!@#$%^&*(),.?":{}|&#60;&#62;)</li>
                </ul>
              </div>
            </div>
            
            <div>
              <label htmlFor="confirmPassword" className="label">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                className="input mt-1"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={handleChange}
              />
            </div>
            
            <div className="flex items-center">
              <input
                id="terms"
                name="terms"
                type="checkbox"
                required
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded dark:bg-gray-800 dark:border-gray-600"
              />
              <label htmlFor="terms" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                I agree to the{' '}
                <Link to="/terms-of-service" className="text-primary-600 hover:text-primary-500 dark:text-primary-400" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy-policy" className="text-primary-600 hover:text-primary-500 dark:text-primary-400" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </Link>
              </label>
            </div>
            
            <div className="flex justify-center pt-2">
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                options={{ appearance: 'interaction-only' }}
                onSuccess={setTurnstileToken}
                onExpire={() => setTurnstileToken(null)}
                onError={() => setTurnstileToken(null)}
              />
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                type="button"
                onClick={prevStep}
                className="btn btn-secondary w-1/2 py-3"
              >
                Back
              </button>
              <button
                type="submit"
                className="btn btn-primary w-1/2 py-3"
                disabled={loading}
              >
                {loading ? (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : null}
                Register
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default Signup;
